/**
 * /share/crs/:companyId — public, no-auth CRS preview rendered from a
 * tokenised URL. Designed to land cleanly in an investor's inbox click.
 *
 * Token comes via the `?t=` query param. Backend HMAC-verifies the token
 * and returns 404 (not 401) on bad tokens so we don't leak existence.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import selfServeService, { PublicCRSPreview } from '../services/selfServeService';


const tierStyle = (tier: string | null): string => {
  switch (tier) {
    case 'investor_ready': return 'bg-green-100 text-green-700';
    case 'nearly_ready': return 'bg-emerald-50 text-emerald-700';
    case 'developing': return 'bg-yellow-50 text-yellow-700';
    case 'early_stage': return 'bg-orange-50 text-orange-700';
    case 'pre_revenue': return 'bg-red-50 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const scoreColor = (s: number | null): string => {
  if (s === null || s === undefined) return 'bg-gray-100 text-gray-500';
  if (s >= 80) return 'bg-green-100 text-green-700';
  if (s >= 60) return 'bg-yellow-100 text-yellow-700';
  if (s >= 40) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};


const PublicCRSPreviewPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const [preview, setPreview] = useState<PublicCRSPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const id = parseInt(companyId || '0', 10);
      if (!id || !token) {
        setError('This link is missing required parameters.');
        setLoading(false);
        return;
      }
      const res = await selfServeService.getPublicCRSPreview(id, token);
      setLoading(false);
      if (res.error) {
        setError(res.error);
        return;
      }
      setPreview(res.data || null);
    })();
  }, [companyId, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-start justify-center px-4 py-10">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-6 h-6 text-indigo-500" />
          <span className="font-semibold text-lg">VentureOS</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <h1 className="text-xl font-semibold">Link is invalid or expired</h1>
            <p className="text-gray-600 mt-2">
              Ask the founder for a fresh share link.
            </p>
          </div>
        ) : preview ? (
          <>
            <h1 className="text-2xl font-semibold">{preview.company_name || 'Company'}</h1>
            {preview.industry && (
              <p className="text-gray-500 text-sm mt-1">{preview.industry}</p>
            )}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
              <div className="sm:col-span-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Capital Readiness Score
                </div>
                {preview.tier && (
                  <span className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded ${tierStyle(preview.tier)}`}>
                    {preview.tier.replace(/_/g, ' ')}
                  </span>
                )}
                {preview.generated_at && (
                  <div className="text-xs text-gray-500 mt-2">
                    Generated {new Date(preview.generated_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <div className={`text-4xl font-semibold w-24 h-24 rounded-lg flex items-center justify-center ${scoreColor(preview.overall_score)}`}>
                  {preview.overall_score?.toFixed(0) ?? '—'}
                </div>
              </div>
            </div>
            {preview.dimension_scores && Object.keys(preview.dimension_scores).length > 0 && (
              <div className="mt-6">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Dimensions
                </div>
                <div className="space-y-2">
                  {Object.entries(preview.dimension_scores).map(([dim, val]) => (
                    <div key={dim}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="capitalize text-gray-700">{dim.replace(/_/g, ' ')}</span>
                        <span className="text-gray-500">{Math.round(val)} / 100</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded">
                        <div
                          className={`h-2 rounded ${scoreColor(val)}`}
                          style={{ width: `${Math.max(0, Math.min(100, val))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-8 pt-4 border-t border-gray-100 text-xs text-gray-400 text-center">
              Powered by VentureOS
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PublicCRSPreviewPage;
